import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

/**
 * Custom validators for enhanced form security and validation
 */
export class CustomValidators {
  /**
   * Email validator - RFC 5322 compliant
   * Validates email format and rejects common typos
   */
  static email(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null; // Don't validate empty values (use Validators.required for that)
      }

      // Reject consecutive dots
      if (control.value.includes('..')) {
        return { invalidEmail: true };
      }

      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      const isValid = emailRegex.test(control.value);

      return isValid ? null : { invalidEmail: true };
    };
  }

  /**
   * Password strength validator
   * Requires: minimum length, uppercase, lowercase, number, special character
   */
  static passwordStrength(minLength: number = 8): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }

      const password = control.value;
      const requirements: string[] = [];

      if (password.length < minLength) {
        requirements.push(`Minimum ${minLength} characters`);
      }

      if (!/[A-Z]/.test(password)) {
        requirements.push('One uppercase letter');
      }

      if (!/[a-z]/.test(password)) {
        requirements.push('One lowercase letter');
      }

      if (!/[0-9]/.test(password)) {
        requirements.push('One number');
      }

      // Removed unnecessary escapes for characters that don't require them within a character class
      // Only \ and - (when not at start/end) and [ ] need escaping.
      if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) {
        requirements.push('One special character');
      }

      return requirements.length > 0
        ? { passwordStrength: { requirements } }
        : null;
    };
  }

  /**
   * XSS prevention validator
   * Detects and rejects potentially dangerous HTML/JavaScript patterns
   */
  static noXSS(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value || typeof control.value !== 'string') {
        return null;
      }

      const value = control.value.toLowerCase();
      const dangerousPatterns = [
        '<script',
        '</script>',
        '<iframe',
        '</iframe>',
        'javascript:', // NOSONAR
        'onerror=',
        'onload=',
        'onclick=',
        '<object',
        '<embed',
        'eval(',
      ];

      const found: string[] = [];

      dangerousPatterns.forEach((pattern) => {
        if (value.includes(pattern)) {
          found.push(pattern);
        }
      });

      return found.length > 0 ? { xssDetected: { found } } : null;
    };
  }

  /**
   * Enhanced required validator that fails on whitespace-only strings
   */
  static requiredTrim(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return { requiredTrim: true };
      }

      if (typeof control.value === 'string' && control.value.trim() === '') {
        return { requiredTrim: true };
      }

      return null;
    };
  }

  /**
   * Alphanumeric validator
   * Allows letters, numbers, spaces, hyphens, underscores, and accented characters
   */
  static alphaNumeric(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }

      // Allow letters (including accented), numbers, spaces, hyphens, underscores, apostrophes
      const alphaNumericRegex = /^[a-zA-ZÀ-ÿ0-9\s\-_']+$/;
      const isValid = alphaNumericRegex.test(control.value);

      return isValid ? null : { alphaNumeric: true };
    };
  }
}
